// 顶部菜单 (⋯): 收纳 语言/主题/回收站/空间利用率/登出
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FileItem } from '../lib/db';
import { formatBytes } from '../lib/utils';
import { makeT, type Lang } from '../lib/i18n';

export function TopMenu({
  lang, theme, items, quota, onToggleLang, onToggleTheme, onShowTrash, onLogout,
}: {
  lang: Lang;
  theme: 'dark' | 'light';
  items: FileItem[];
  quota: { usedBytes: number; fileCount: number; limitBytes: number } | null;
  onToggleLang: () => void;
  onToggleTheme: () => void;
  onShowTrash: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = makeT(lang);

  const used = quota?.usedBytes ?? 0;
  const total = quota?.limitBytes ?? 10 * 1024 * 1024 * 1024;
  const pct = total > 0 ? (used / total) * 100 : 0;

  // 点击外部关闭菜单
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const menuItem = (label: string, onClick?: () => void, extra?: string) => (
    <button className="menu-item" onClick={() => { onClick?.(); if (!extra) setOpen(false); }}>
      <span>{label}</span>
      {extra && <span className="menu-extra">{extra}</span>}
    </button>
  );

  return (
    <>
      <div ref={ref} style={{ position: 'relative' }}>
        <button className="icon-btn" onClick={() => setOpen((o) => !o)} title="Menu">⋯</button>
        {open && (
          <div className="top-menu">
            {menuItem(t('header.lang'), () => onToggleLang(), lang === 'zh' ? '' : 'EN')}
            {menuItem(t('header.theme'), () => onToggleTheme(), theme === 'dark' ? '☀️' : '🌙')}
            {menuItem(t('header.trash'), onShowTrash)}
            {menuItem(t('header.storage'), () => { setSpaceOpen(true); setOpen(false); }, Math.round(pct) + '%')}
            <div className="menu-sep" />
            {menuItem(t('header.logout'), onLogout)}
          </div>
        )}
      </div>

      {/* 空间利用率详情抽屉 (portal到body) */}
      {spaceOpen && createPortal(
        <div className="detail-panel" style={{ zIndex: 70, top: 0, bottom: 0 }}>
          <button className="close" onClick={() => setSpaceOpen(false)}>×</button>
          <h3 style={{ marginTop: 8 }}>{t('header.storage')}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, margin: '20px 0' }}>
            <Ring pct={pct} size={120} stroke={12} />
            <div style={{ flex: 1 }}>
              <div className="row"><span className="k">{t('stats.used')}</span><span className="v">{formatBytes(used)}</span></div>
              <div className="row"><span className="k">{t('stats.quota')}</span><span className="v">{formatBytes(total)}</span></div>
              <div className="row"><span className="k">{lang === 'zh' ? '剩余' : 'Free'}</span><span className="v">{formatBytes(Math.max(0, total - used))}</span></div>
              <div className="row"><span className="k">{t('stats.files')}</span><span className="v">{quota?.fileCount ?? 0}</span></div>
            </div>
          </div>
          <ByType items={items} lang={lang} />
        </div>,
        document.body
      )}
    </>
  );
}

function Ring({ pct, size = 40, stroke = 4 }: { pct: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  const filled = circ * (p / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--accent)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${filled} ${circ - filled}`} transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" fill="var(--fg)" fontSize={size/3.2} fontWeight={600}>{Math.round(p)}%</text>
    </svg>
  );
}

function ByType({ items, lang }: { items: FileItem[]; lang: Lang }) {
  const catLabels: Record<string, string> = {
    image: lang === 'zh' ? '图片' : 'Images',
    video: lang === 'zh' ? '视频' : 'Videos',
    audio: lang === 'zh' ? '音频' : 'Audio',
    other: lang === 'zh' ? '其他' : 'Other',
  };
  const c = { image: { count: 0, bytes: 0 }, video: { count: 0, bytes: 0 }, audio: { count: 0, bytes: 0 }, other: { count: 0, bytes: 0 } };
  for (const it of items) {
    const b = it.size || 0;
    if (it.mime.startsWith('image/')) { c.image.count++; c.image.bytes += b; }
    else if (it.mime.startsWith('video/')) { c.video.count++; c.video.bytes += b; }
    else if (it.mime.startsWith('audio/')) { c.audio.count++; c.audio.bytes += b; }
    else { c.other.count++; c.other.bytes += b; }
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{lang === 'zh' ? '分类明细' : 'By Type'}</div>
      {(Object.keys(c) as (keyof typeof c)[]).map((k) => (
        <div className="row" key={k}>
          <span className="k">{catLabels[k]} · {c[k].count}</span>
          <span className="v">{formatBytes(c[k].bytes)}</span>
        </div>
      ))}
    </div>
  );
}
